// This file uniquely implemented due to its usage. See README
import './EditPanel.modal.scss';
import { equipmentPrettyType } from 'Utils/filters';
import equipGridOptions from './equipmentGrid.value';
import { EventEmitter } from '@angular/core';
import Events from '../../../ng-app/components/dynamic-forms/form-schemas/panels/events';
import EquipmentMap from '../../../ng-app/components/dynamic-forms/form-schemas/panels/equipmentMap';
import template from './EditPanel.modal.html';
import _ from 'lodash';

/* @ngInject */
function EditPanelsModalController(
  $uibModalInstance,
  EquipmentInfoService,
  DashboardService,
  PanelTypes,
  panels,
  selectedPanelId,
  $scope,
  EventInfoService,
  Organization
) {
  const vm = this;
  /* Private Variables */
  const equipmentsForPanels = {};
  const topOfRange = 21;

  /* Public Variables */
  vm.panels = angular.copy(panels);
  vm.activePanelId = selectedPanelId;
  vm.availablePanelsToAdd = Object.fromEntries(
    Object
      .entries(PanelTypes)
      .sort(([, a], [, b]) => a.prettyName > b.prettyName ? 1 : -1)
  );
  vm.newPanelType = null;
  vm.numPanelsToAdd = 1;
  vm.numPanelsToAddOptions = _.range(1, topOfRange); // create array with numbers 1-20
  vm.configIsValid = true;
  vm.formOptions = {};
  vm.notifyChange = new EventEmitter();
  /** @type {Object.<string, string>} Shown when hovering over a panel card */
  vm.tooltips = {};

  /* Public Functions */
  vm.$onInit = $onInit;
  vm.addPanelToObject = addPanelToObject;
  vm.cancel = cancel;
  vm.lengthOf = lengthOf;
  vm.removePanel = removePanel;
  vm.save = save;
  vm.setActivePanel = setActivePanel;
  vm.updateConfigForEquipmentChange = updateConfigForEquipmentChange;
  vm.scrollTo = scrollTo;
  vm.getAllowedEquipments = getAllowedEquipments;
  vm.getPrettyName = getPrettyName;
  vm.updateSettings = updateSettings;
  vm.onFormFeedback = onFormFeedback;
  vm.eventSchema = _.cloneDeep(Events);

  (async function init() {
    // Options object passed into the ui-grid directive
    vm.gridOptions = equipGridOptions;
    vm.gridOptions.appScopeProvider = vm;
    vm.gridOptions.onRegisterApi = (gridApi) => {
      vm.gridApi = gridApi;

      gridApi.selection.on.rowSelectionChangedBatch($scope, onSelectionChanged);

      gridApi.selection.on.rowSelectionChanged($scope, onSelectionChanged);
    };

    vm.organizations = await Organization.model;

    if (selectedPanelId) {
      $uibModalInstance.rendered.then(() => {
        setActivePanel(selectedPanelId);
        scrollTo(selectedPanelId);
      });
    }
  })();

  function $onInit() {
    for (const panelEntry of Object.entries(vm.panels)) {
      generateTooltip(...panelEntry);
    }

    $scope.$watch(() => vm.updatedOrg, () => {
      const id = vm.updatedOrg;
      if (vm.allEqCopy) {
        setupEquipmentList(vm.allEqCopy, id);
        initiateTable();
      }

    });
  }

  function setSchema() {
    const panel = vm.panels[vm.activePanelId];
    // Equipment Map panels have their own config form (GPS history defaults);
    // every other panel type keeps the shared events schema.
    const eventSchema = _.cloneDeep(panel.type === 'equipmentMap' ? EquipmentMap : Events);
    if (panel.type === 'events') {
      const selectedEquipmentTypes = new Set(_.map(vm.gridApi.selection.getSelectedRows(), 'equipmentType'));
      const eventExclusions = new Set(EventInfoService.eventsConfig.eventPanelExclusions);

      _.forEach(EventInfoService.eventsConfig.definitions, ({ eventName, title: label }) => {
        if (!eventExclusions.has(eventName) && selectedEquipmentTypes.has(eventName.split('/')[0])) {
          _.forEach(
            eventSchema.components,
            (comp) => {
              if (comp.type === 'select') {
                comp.data.values.push({ label, value: eventName });
              }
            }
          );
        }
      });
      // Sort events by label instead of value
      eventSchema.components.forEach(({ data, key, type }) => {
        if (type !== 'select') { return }
        data.values = _.sortBy(data.values, ['label', 'value']);
        if (_.isArray(panel.config?.[key])) {
          panel.config[key] = _.intersection(_.map(data.values, 'value'), panel.config[key]);
        }
      });
    }

    vm.eventSchema = eventSchema;
  }

  function onSelectionChanged(rows, event = {}) {
    rows = _.castArray(rows);
    _.forEach(rows, (row) => {
      row.entity.selectedForPanel = row.isSelected;
      event.ignoreThis || updateConfigForEquipmentChange(row.entity);
    });
    loadGridData();
    vm.gridApi.core.refresh();
    event.ignoreThis || setSchema();
  }

  function addPanelToObject() {
    for (let i = 0; i < vm.numPanelsToAdd; i += 1) {
      const tempId = `TEMPID-${lengthOf(vm.panels)}`;
      const newPanel = {
        config: {},
        equipment: [],
        type: vm.newPanelType
      };

      vm.panels[tempId] = newPanel;
      generateTooltip(tempId, newPanel);
    }
    vm.newPanelType = null;
    vm.numPanelsToAdd = 1;
  }

  function cancel() {
    $uibModalInstance.dismiss('cancelled');
  }

  function getAllowedEquipments() {
    vm.allowedEqLoaded = false;
    const { allowedEquipmentTypes } = PanelTypes[vm.panels[vm.activePanelId].type];
    const fetchNodes = _.isEmpty(allowedEquipmentTypes) ?
      EquipmentInfoService.getAllEquipments() :
      EquipmentInfoService.getEquipmentByType(allowedEquipmentTypes);
    return fetchNodes
      .then((equipment) => {
        vm.allEqCopy = angular.copy(equipment);
        return vm.allEqCopy;
      })
      .then(setupEquipmentList)
      .then(initiateTable);
  }

  function setupEquipmentList(equipment, orgId) {
    equipment = angular.copy(equipment);

    const eqToSelect = vm.panels[vm.activePanelId].equipment;
    _.forEach(equipment, (eq) => { eq.selectedForPanel = eqToSelect.includes(eq.id) });

    // Check for selected-assigned-eq mismatch (occurs when eq is deleted)
    const selected = _.map(_.filter(equipment, 'selectedForPanel'), 'id');
    if (selected.length !== eqToSelect.length) {
      // "Fix" the panel's zombie eq (once modal is closed -> board saved)
      _.pull(eqToSelect, ..._.difference(eqToSelect, selected));
      // TODO: Remove this patch if API updates panels during equipment deletion
    }

    if (orgId) {
      equipment = _.filter(equipment, eq => eq.org === orgId);
    }

    return equipmentsForPanels[vm.activePanelId] = equipment;
  }

  function initiateTable() {
    vm.gridApi.selection.clearSelectedRows({ ignoreThis: true });
    loadGridData();
    // It allows to modify the selection
    vm.gridApi.grid.modifyRows(vm.gridOptions.data);

    const equipmentSelected = _.filter(vm.gridOptions.data, 'selectedForPanel');

    _.forEach(equipmentSelected, (eq) => {
      vm.gridApi.selection.selectRow(eq, { ignoreThis: true });
    });
  }

  function loadGridData() {
    _.set(vm, 'gridOptions.data', _.orderBy(equipmentsForPanels[vm.activePanelId], ['selectedForPanel'], ['desc']));
  }

  function generateTooltip(panelId, panel) {
    const MAX_TOOLTIP_DEVICES = 10;
    let devices = panel.equipment
      .map(eq => EquipmentInfoService.getEquipment(eq)?.name)
      .filter(eq => !_.isNil(eq))
      .sort((a, b) => a.localeCompare(b));

    devices = devices.length <= MAX_TOOLTIP_DEVICES ? devices : [
      ...devices.slice(0, MAX_TOOLTIP_DEVICES),
      `( and ${devices.length - MAX_TOOLTIP_DEVICES} others… )`,
    ];

    if (_.isEmpty(devices)) {
      devices = ['No devices assigned'];
    }

    vm.tooltips[panelId] = devices;
  }

  function setActivePanel(panelId) {
    vm.activePanelId = panelId;
    generateFormInfo();
    vm.getAllowedEquipments()
      .then(setSchema);
  }

  function lengthOf(obj) {
    return _.size(obj);
  }

  function removePanel(panelId) {
    if (vm.panels[panelId].id) {
      vm.panels[panelId].deleted = true;
    } else {
      delete vm.panels[panelId];
    }
    if (equipmentsForPanels[panelId]) {
      delete equipmentsForPanels[panelId];
    }
    if (panelId === vm.activePanelId) {
      vm.activePanelId = null;
    }
  }

  function save() {
    _.forEach(vm.panels, (panel) => {
      if (panel.id) {
        if (panel.deleted) {
          DashboardService.removePanelFromDashboard(panel.id);
        } else {
          DashboardService.updatePanelOnDashboard(panel);
        }
      } else {
        DashboardService.addPanelToDashboard(panel);
      }
    });

    $uibModalInstance.close(vm.panels);
  }

  function updateConfigForEquipmentChange(equipment) {
    const daPanel = vm.panels[vm.activePanelId];
    if (equipment.selectedForPanel) {
      if (!_.includes(daPanel.equipment, equipment.id)) {
        daPanel.equipment.push(equipment.id);
      }
      if (!daPanel.config[equipment.id] && _.has(PanelTypes, [daPanel.type, 'getDefaultPanelConfigPerEquipment'])) {
        const eqSubConfig = {
          name: equipment.name,
          ...angular.copy(
            PanelTypes[daPanel.type].getDefaultPanelConfigPerEquipment(equipment)
          )
        };
        _.set(daPanel.config, equipment.id, eqSubConfig);
      }
    } else {
      equipment.selectedForPanel = false;
      const ind = daPanel.equipment.indexOf(equipment.id);
      if (ind > -1) {
        daPanel.equipment.splice(ind, 1);
      }
      if (daPanel.config[equipment.id]) {
        delete daPanel.config[equipment.id];
      }
    }
    generateTooltip(vm.activePanelId, daPanel);
    triggerNotifyChange();
  }

  function scrollTo(selectedPanId) {
    const target = angular.element(`#${selectedPanId}`);
    angular.element('#equipment-card-container').animate({
      scrollTop: target.position().top
    }, {
      duration: 500,
      step(__, tween) {
        tween.end = target.position().top;
      }
    });
  }

  function getPrettyName(equipment) {
    return equipmentPrettyType(equipment);
  }

  function triggerNotifyChange() {
    vm.notifyChange.emit({
      model: vm.panels[vm.activePanelId].config
    });
  }

  function updateSettings(update) {
    const { id, model } = update;
    $scope.$apply(() => {
      // keep original reference so we don't cause an infinite change loop
      const { config } = vm.panels[id];
      model !== config && angular.copy(model, config);
    });
  }

  function onFormFeedback(event) {
    if (event.type !== 'isValid') { return }

    // TODO: Move this validation to the form schema
    const { config = {}, type } = vm.panels[vm.activePanelId];
    if (type === 'events') {
      const equipmentAssigned = _.uniq(vm.gridApi.selection.getSelectedRows().map(eq => eq.equipmentType));
      const availableEventTypes = _.uniq(config.availableEvents?.map(evt => evt.split('/')[0]));
      const defaultEventTypes = _.uniq(config.defaultEvents?.map(evt => evt.split('/')[0]));

      // Check if all the events in defaultEvents are included in availableEvents array
      if (
        !_.isEmpty(config.defaultEvents) &&
        !_.isEmpty(config.availableEvents) &&
        !config.defaultEvents?.every(defEvent => config.availableEvents?.includes(defEvent))
      ) {
        // let add them for the user
        const notIncluded = _.differenceWith(config.defaultEvents, config.availableEvents, _.isEqual);

        config.availableEvents = _.union(config.availableEvents, notIncluded);

        triggerNotifyChange();
      }

      $scope.$apply(() => {
        vm.configIsValid =
          (!_.isEmpty(defaultEventTypes) && equipmentAssigned.every(eq => defaultEventTypes.includes(eq))) ||
          (!_.isEmpty(availableEventTypes) && equipmentAssigned.every(eq => availableEventTypes.includes(eq))) ||
          _.isEmpty([...availableEventTypes, ...defaultEventTypes])
        ;
      });

    } else {
      $scope.$apply(() => {
        vm.configIsValid = event.data;
      });
    }
  }

  function generateFormInfo() {
    vm.formOptions = {
      title: 'Config',
      buildFromSchema: vm.panels[vm.activePanelId].type === 'events',
      formType: `panels.${vm.panels[vm.activePanelId].type}`,
    };
  }
}

export default {
  template,
  controller: EditPanelsModalController,
  controllerAs: 'EditPanelsCtrl'
};
